<template>
  <section class="booking-section" ref="bookingSection" id="form">
    <div class="booking-container">
      <div class="booking-header">
        <h2 class="section-title">Book Your Table</h2>
        <p class="section-subtitle">Reserve your spot and enjoy the Brewtopia experience</p>
      </div>

      <form class="booking-form" @submit.prevent="handleSubmit">
        <div class="form-row full-width">
          <div class="form-group">
            <label for="name">Name *</label>
            <input 
              type="text" 
              id="name" 
              v-model="formData.name" 
              placeholder="Your full name"
              required
            >
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="phone">Phone Number *</label>
            <input 
              type="tel" 
              id="phone" 
              v-model="formData.phone" 
              placeholder="+1 (555) 000-0000"
              required
            >
          </div>

          <div class="form-group">
            <label for="email">E-Mail Address</label>
            <input 
              type="email" 
              id="email" 
              v-model="formData.email" 
              placeholder="your@email.com"
            >
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="date">Date *</label>
            <input 
              type="date" 
              id="date" 
              v-model="formData.date" 
              required
            >
          </div>

          <div class="form-group">
            <label for="time">Time *</label>
            <input 
              type="time" 
              id="time" 
              v-model="formData.time" 
              required
            >
          </div>

          <div class="form-group">
            <label for="guests">Guests *</label>
            <select id="guests" v-model="formData.guests" required>
              <option value="">Select</option>
              <option value="1">1 Guest</option>
              <option value="2">2 Guests</option>
              <option value="3">3 Guests</option>
              <option value="4">4 Guests</option>
              <option value="5">5 Guests</option>
              <option value="6">6+ Guests</option>
            </select>
          </div>
        </div>

        <div class="form-row full-width">
          <div class="form-group">
            <label for="special">Special Request</label>
            <textarea 
              id="special" 
              v-model="formData.specialRequest" 
              placeholder="Any special requirements or dietary restrictions..."
              rows="4"
            ></textarea>
            <small class="form-note">We recommend booking at least 1 hour in advance</small>
          </div>
        </div>

        <button type="submit" class="submit-btn">Reserve Now</button>

        <p class="confirmation-note">
          We'll confirm your reservation within 1 hour via email or phone
        </p>

        <p class="required-note">Fields marked with * are required</p>
      </form>
    </div>

    <!-- Success Popup -->
    <transition name="popup">
      <div v-if="showPopup" class="popup-overlay" @click="closePopup">
        <div class="popup-container" @click.stop>
          <div class="popup-icon">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#D4A574" stroke-width="2" fill="none"/>
              <path d="M8 12L11 15L16 9" stroke="#D4A574" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          
          <h3 class="popup-title">Thank You!</h3>
          <p class="popup-message">
            Your reservation has been received. We'll contact you within 1 hour to confirm.
          </p>

          <button @click="closePopup" class="popup-btn">Got it!</button>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
export default {
  name: 'BookingForm',
  data() {
    return {
      showPopup: false,
      formData: {
        name: '',
        phone: '',
        email: '',
        date: '',
        time: '',
        guests: '',
        specialRequest: ''
      }
    }
  },
  mounted() {
    this.observeSection();
  },
  methods: {
  addItemToRequest(itemName) {
    if (this.formData.specialRequest) {
      this.formData.specialRequest += `, ${itemName}`;
    } else {
      this.formData.specialRequest = `I'd like to order: ${itemName}`;
    }
    
    this.$nextTick(() => {
      const textarea = document.getElementById('special');
      if (textarea) {
        textarea.focus();
      }
    });
  },
    handleSubmit() {
      console.log('Form submitted:', this.formData);
      this.showPopup = true;
    },
    closePopup() {
      this.showPopup = false;
      this.resetForm();
    },
    resetForm() {
      setTimeout(() => {
        this.formData = {
          name: '',
          phone: '',
          email: '',
          date: '',
          time: '',
          guests: '',
          specialRequest: ''
        };
      }, 300);
    },
    observeSection() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, {
        threshold: 0.1
      });

      observer.observe(this.$refs.bookingSection);
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.booking-section {
  width: 100%;
  padding: 6rem 2rem;
  background-color: #FFF8DC;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
  overflow-x: hidden;
  position: relative;
}

.booking-section.visible {
  opacity: 1;
  transform: translateY(0);
}

.booking-container {
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  background-color: #FFFFFF;
  padding: 3rem;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(111, 78, 55, 0.1);
}

.booking-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 600;
  color: #6F4E37;
  margin-bottom: 0.75rem;
}

.section-subtitle {
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  color: #8B7355;
}

.booking-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  width: 100%;
}

.form-row.full-width {
  grid-template-columns: 1fr;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
}

label {
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  color: #6F4E37;
}

input, select, textarea {
  width: 100%;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  padding: 0.75rem 1rem;
  border: 1.5px solid #E8DCC4;
  border-radius: 8px;
  background-color: #FEFDFB;
  color: #6F4E37;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: #D4A574;
  box-shadow: 0 0 0 3px rgba(212, 166, 116, 0.1);
}

input::placeholder, textarea::placeholder {
  color: #C4B5A0;
}

textarea {
  resize: vertical;
  min-height: 100px;
}

.form-note {
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  color: #8B7355;
  margin-top: 0.25rem;
}

.submit-btn {
  padding: 14px 2.5rem;
  background-color: #D4A574;
  color: #8B4513;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.4s ease;
  margin-top: 1rem;
}

.submit-btn:hover {
  background-color: #8B4513;
  color: #D4A574;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(139, 69, 19, 0.3);
}

.confirmation-note {
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  text-align: center;
  color: #8B7355;
  margin-top: 1rem;
}

.required-note {
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  text-align: center;
  color: #8B7355;
  margin-top: 0.5rem;
}

/* Popup Styles */
.popup-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  min-height: 100%;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 9999;
  padding: 2rem;
}

.popup-container {
  background: #FFFFFF;
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(111, 78, 55, 0.5);
  padding: 2rem 1.5rem;
  text-align: center;
  position: relative;
  margin-bottom: 18rem;
}

.popup-icon {
  margin: 0 auto 1.5rem;
  animation: checkmark 0.6s ease-out;
}

@keyframes checkmark {
  0% {
    transform: scale(0) rotate(-45deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(5deg);
  }
  100% {
    transform: scale(1) rotate(0);
    opacity: 1;
  }
}

.popup-title {
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  font-weight: 600;
  color: #6F4E37;
  margin-bottom: 1rem;
}

.popup-message {
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  line-height: 1.6;
  color: #8B7355;
  margin-bottom: 2rem;
}

.popup-btn {
  width: 100%;
  max-width: 200px;
  padding: 12px 2rem;
  background-color: #D4A574;
  color: #8B4513;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.popup-btn:hover {
  background-color: #8B4513;
  color: #D4A574;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(139, 69, 19, 0.3);
}

/* Popup Animations */
.popup-enter-active {
  animation: popup-in 0.3s ease-out;
}

.popup-leave-active {
  animation: popup-out 0.2s ease-in;
}

@keyframes popup-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes popup-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.popup-enter-active .popup-container {
  animation: scale-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.popup-leave-active .popup-container {
  animation: scale-out 0.2s ease-in;
}

@keyframes scale-in {
  0% {
    transform: scale(0.7);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes scale-out {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0.8);
    opacity: 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .booking-section {
    padding: 4rem 1.5rem;
  }

  .booking-container {
    padding: 2rem 1.5rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }

  .popup-container {
    padding: 1.5rem;
    max-width: 350px;
    margin-bottom: 12rem;
  }

  .popup-title {
    font-size: 1.75rem;
  }

  .popup-message {
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }

  .popup-icon svg {
    width: 50px;
    height: 50px;
  }

  .popup-btn {
    font-size: 15px;
  }
}
</style>